<!--
 *@Description: 首页（第1页）
 * 顶部：第一个分类的置顶文章（2/3图 + 1/3文字）+ 3篇最新均分
 * 下面：最新文章列表 + 分页器
 * 
 * 布局说明：置顶区域图片占2格宽度，文字占1格宽度，与下方3卡片完美对齐
-->
<script setup lang="ts">
const config = useRuntimeConfig()

interface Article {
  ArticleId: number
  Title: string
  Slug: string
  Summary: string | null
  CoverImage: string | null
  CoverBlurHash: string | null
  TypeId: number
  TypeName?: string
  TypeSlug?: string
  Renderer?: string
  IsPublic: number
  IsPinned: number
  IsTop: number
  CreatedAt: string
}

interface ArticleType {
  typeId: number
  name: string
  slug: string
  icon: string | null
  description: string | null
}

interface Featured {
  type: ArticleType
  pinnedArticle: Article | null
  latestArticles: Article[]
}

interface Latest {
  articles: Article[]
  total: number
  page: number
  limit: number
  totalPages: number
  hasMore: boolean
}

interface HomeResponse {
  featured: Featured | null
  latest: Latest
}

/**
 * 【性能优化】带缓存的数据获取
 * 使用 useState 做全局缓存，缓存 10 分钟避免频繁请求
 */
const CACHE_DURATION = 20 * 60 * 1000 // 10 分钟

interface CachedData {
  data: HomeResponse
  timestamp: number
}

// 全局缓存状态
const homeCache = useState<CachedData | null>('home-page-1-cache', () => null)

// 检查缓存是否有效
const isCacheValid = (cache: CachedData | null): cache is CachedData => {
  if (!cache) return false
  return Date.now() - cache.timestamp < CACHE_DURATION
}

// 获取首页数据（始终获取第1页）
const { data, status, error } = await useAsyncData(
  'home-page-1',
  async () => {
    // 检查缓存
    if (isCacheValid(homeCache.value)) {
      return homeCache.value.data
    }
    
    // 请求新数据
    const result = await $fetch<HomeResponse>(`${config.public.apiBase}/home`, {
      query: { page: 1 }
    })
    
    // 更新缓存
    homeCache.value = {
      data: result,
      timestamp: Date.now()
    }
    
    return result
  },
  { server: true, dedupe: 'defer' }
)

const featured = computed(() => data.value?.featured)
const latest = computed(() => data.value?.latest)

// 工具函数
const getImageUrl = (key: string | null, width = 800) => {
  if (!key) return ''
  if (key.startsWith('http')) return key
  return `https://image.lixiang.page/cdn-cgi/image/format=auto,quality=80,width=${width}/${key}`
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// 分页导航（首页点击去 /page/x）
const goToPage = (page: number) => {
  if (page < 1 || page > (latest.value?.totalPages || 1)) return
  if (page === 1) {
    navigateTo('/')
  } else {
    navigateTo(`/page/${page}`)
  }
}

// 生成分页数组
const pageNumbers = computed(() => {
  const total = latest.value?.totalPages || 1
  const pages: (number | string)[] = []

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i)
  } else {
    pages.push(1)
    if (1 > 3) pages.push('...')

    const start = Math.max(2, 1 - 1)
    const end = Math.min(total - 1, 1 + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    if (1 < total - 2) pages.push('...')
    pages.push(total)
  }

  return pages
})

// SEO
useHead({
  title: 'LiXiang\'s Page',
  meta: [
    { name: 'description', content: 'LiXiang 的个人博客，记录旅行游记、技术文章与生活随笔。' }
  ]
})
</script>

<template>
  <div class="home-container">
    <!-- 加载状态 -->
    <div v-if="status === 'pending'" class="loading">
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
        <path fill="#5B6FB5"
          d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z">
          <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25"
            dur=".8s" repeatCount="indefinite" />
        </path>
      </svg>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error">
      <p>加载失败，请刷新重试</p>
    </div>

    <!-- 内容区域 -->
    <div v-else>
      <!-- ========== 特色区：第一个分类 ========== -->
      <section v-if="featured" class="featured-section">
        <!-- 置顶文章：使用grid实现2:1布局 -->
        <div v-if="featured.pinnedArticle" class="pinned-row">
          <NuxtLink :to="`/blog/${featured.pinnedArticle.Slug}`" class="pinned-image-wrapper">
            <div class="pinned-image">
              <ClientOnly>
                <BlurhashImage v-if="featured.pinnedArticle.CoverImage"
                  :src="getImageUrl(featured.pinnedArticle.CoverImage, 1200)"
                  :hash="featured.pinnedArticle.CoverBlurHash || 'L6PZfSi_.AyE_3t7t7R**0o#DgR4'"
                  :alt="featured.pinnedArticle.Title" class="cover-image" />
                <div v-else class="placeholder-image">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1">
                    <rect x="3" y="3" width="18" height="18" rx="0" ry="0" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
              </ClientOnly>
            </div>
          </NuxtLink>
          <NuxtLink :to="`/blog/${featured.pinnedArticle.Slug}`" class="pinned-content">
            <div class="pinned-badges">
              <span v-if="featured.pinnedArticle.IsTop === 1 || featured.pinnedArticle.IsPinned === 1"
                class="pinned-badge">置顶</span>
              <span v-if="featured.pinnedArticle.IsPublic === 0" class="private-badge">私密</span>
            </div>
            <h2 class="pinned-title">{{ featured.pinnedArticle.Title }}</h2>
            <p class="pinned-summary">{{ featured.pinnedArticle.Summary || '暂无摘要' }}</p>
            <div class="pinned-meta">
              <span class="meta-date">{{ formatDate(featured.pinnedArticle.CreatedAt) }}</span>
            </div>
          </NuxtLink>
        </div>

        <!-- 最新3篇：均分一行，与上方图片对齐 -->
        <div v-if="featured.latestArticles.length > 0" class="latest-grid">
          <NuxtLink v-for="article in featured.latestArticles" :key="article.ArticleId" :to="`/blog/${article.Slug}`"
            class="latest-card">
            <div class="latest-image">
              <ClientOnly>
                <BlurhashImage v-if="article.CoverImage" :src="getImageUrl(article.CoverImage, 600)"
                  :hash="article.CoverBlurHash || 'L6PZfSi_.AyE_3t7t7R**0o#DgR4'" :alt="article.Title"
                  class="cover-image" />
                <div v-else class="placeholder-image">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1">
                    <rect x="3" y="3" width="18" height="18" rx="0" ry="0" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
              </ClientOnly>
            </div>
            <div class="latest-content">
              <h3 class="latest-title">
                <span v-if="article.IsPublic === 0" class="lock-icon" title="私密">🔒</span>
                {{ article.Title }}
              </h3>
              <p class="latest-summary">{{ article.Summary || '暂无摘要' }}</p>
              <span class="latest-date">{{ formatDate(article.CreatedAt) }}</span>
            </div>
          </NuxtLink>
        </div>
      </section>

      <!-- ========== 最新文章列表 ========== -->
      <section v-if="latest && latest.articles.length > 0" class="latest-section">
        <h2 class="section-title">最新文章</h2>

        <div class="article-list">
          <NuxtLink v-for="article in latest.articles" :key="article.ArticleId" :to="`/blog/${article.Slug}`"
            class="article-item">
            <div class="article-content">
              <h3 class="article-title">
                <span v-if="article.IsPublic === 0" class="lock-icon" title="私密">🔒</span>
                {{ article.Title }}
              </h3>
              <div class="article-tags">
                <span v-if="article.TypeName" class="type-tag">{{ article.TypeName }}</span>
              </div>
              <p class="article-summary">{{ article.Summary || '暂无摘要' }}</p>
              <span class="article-date"><ClientOnly>{{ formatDate(article.CreatedAt) }}</ClientOnly></span>
            </div>
            <div class="article-image">
              <ClientOnly>
                <BlurhashImage v-if="article.CoverImage" :src="getImageUrl(article.CoverImage, 400)"
                  :hash="article.CoverBlurHash || 'L6PZfSi_.AyE_3t7t7R**0o#DgR4'" :alt="article.Title"
                  class="cover-image" />
                <div v-else class="placeholder-image">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1">
                    <rect x="3" y="3" width="18" height="18" rx="0" ry="0" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
              </ClientOnly>
            </div>
          </NuxtLink>
        </div>

        <!-- 分页器 -->
        <nav v-if="latest.totalPages > 1" class="pagination">
          <div class="page-numbers">
            <template v-for="(page, idx) in pageNumbers" :key="idx">
              <span v-if="page === '...'" class="page-ellipsis">...</span>
              <button v-else class="page-num" :class="{ active: page === 1 }" @click="goToPage(page as number)">
                {{ page }}
              </button>
            </template>
          </div>

          <button class="page-btn next" @click="goToPage(2)">
            下一页 →
          </button>
        </nav>
      </section>
    </div>
  </div>
</template>

<style scoped>
.home-container {
  max-width: 1000px;
  margin: 0 auto;
}

.loading,
.error {
  text-align: center;
  padding: 100px 20px;
  color: #888;
}

/* ==================== 特色区 ==================== */
.featured-section {
  margin-bottom: 60px;
}

/* 置顶区域：使用3列grid，图片占2列，与下方3卡片完美对齐 */
.pinned-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-bottom: 24px;
}

.pinned-image-wrapper {
  text-decoration: none;
}

.pinned-image {
  width: 100%;
  aspect-ratio: 16/10;
  overflow: hidden;
  background: #f5f5f5;
}

.pinned-image .cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pinned-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-decoration: none;
  color: inherit;
}

.pinned-content:hover .pinned-title {
  color: #5B6FB5;
}

.pinned-badges {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.pinned-badge {
  display: inline-block;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #222;
  color: #fff;
}

.private-badge {
  display: inline-block;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #f0f0f0;
  color: #666;
}

.pinned-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 12px 0;
  transition: color 0.2s;
}

.pinned-summary {
  font-size: 0.95rem;
  line-height: 1.7;
  color: #555;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pinned-meta {
  font-size: 0.875rem;
  color: #888;
}

/* 3篇卡片：与置顶区域使用相同的grid系统 */
.latest-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

.latest-card {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s;
}

.latest-card:hover {
  opacity: 0.85;
}

.latest-image {
  width: 100%;
  aspect-ratio: 16/10;
  overflow: hidden;
  background: #f5f5f5;
  margin-bottom: 12px;
}

.latest-image .cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.latest-content {
  flex: 1;
}

.latest-title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  margin: 0 0 8px 0;
}

.lock-icon {
  font-size: 0.85em;
  margin-right: 4px;
}

.latest-summary {
  font-size: 0.875rem;
  line-height: 1.6;
  color: #666;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.latest-date {
  font-size: 0.8rem;
  color: #999;
}

/* ==================== 最新文章列表 ==================== */
.latest-section {

  padding-top: 1rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 24px 0;
}

.article-list {
  display: flex;
  flex-direction: column;
}

.article-item {
  display: flex;
  gap: 20px;
  padding: 20px 0;
  border-bottom: 1px solid #eaeaea;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s;
}

.article-item:last-child {
  border-bottom: none;
}

.article-item:hover {
  opacity: 0.85;
}

.article-content {
  flex: 1;
  min-width: 0;
}

.article-title {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
  margin: 0 0 8px 0;
  color: var(--text-color, #222);
}

.article-tags {
  margin-bottom: 8px;
}

.type-tag {
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.75rem;
  color: #666;
  background: #f5f5f5;
  border-radius: 2px;
}

.article-summary {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #666;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.article-date {
  font-size: 0.8rem;
  color: #999;
}

.article-image {
  width: 120px;
  height: 80px;
  flex-shrink: 0;
  overflow: hidden;
  background: #f5f5f5;
}

.article-image .cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ==================== 分页器 ==================== */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #eaeaea;
}

.page-btn {
  padding: 8px 16px;
  font-size: 0.9rem;
  color: #666;
  background: none;
  border: 1px solid #ddd;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  color: #333;
  border-color: #999;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-num {
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: #666;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.page-num:hover {
  color: #333;
}

.page-num.active {
  color: #5B6FB5;
  font-weight: 600;
}

.page-ellipsis {
  padding: 0 8px;
  color: #999;
}

/* 占位图 */
.placeholder-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #ddd;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-sizing: border-box;

}

/* ==================== 响应式：桌面端实现2:1对齐 ==================== */
@media (min-width: 640px) {

  /* 三卡片均分 */
  .latest-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 768px) {

  /* 置顶区域：使用3列grid，图片跨2列，与下方3卡片完美对齐 */
  .pinned-row {
    grid-template-columns: repeat(3, 1fr);
  }

  .pinned-image-wrapper {
    grid-column: span 2;
    /* 图片占前2列 */
  }

  .pinned-image {
    aspect-ratio: auto;
    height: 100%;
    min-height: 280px;
  }

  .pinned-title {
    font-size: 1.75rem;
  }

  .article-image {
    width: 180px;
    height: 120px;
  }
}

@media (min-width: 1024px) {
  .pinned-image {
    min-height: 320px;
  }

  .pinned-title {
    font-size: 2rem;
  }

  .pinned-summary {
    -webkit-line-clamp: 4;
    line-clamp: 4;
  }
}

/* ==================== 深色模式 ==================== */
@media (prefers-color-scheme: dark) {
  .pinned-badge {
    background: #fff;
    color: #000;
  }

  .private-badge {
    background: #333;
    color: #aaa;
  }

  .type-tag {
    background: #2a2a2a;
    color: #aaa;
  }

  .placeholder-image {
    background: #2a2a2a;
    color: #444;
  }



  .article-item {
    border-bottom-color: #333;
  }

  .pagination {
    border-top-color: #333;
  }

  .page-btn {
    border-color: #444;
    color: #aaa;
  }

  .page-btn:hover:not(:disabled) {
    border-color: #666;
    color: #fff;
  }

  .page-num {
    color: #aaa;
  }

  .page-num:hover {
    color: #fff;
  }
}
</style>
